import { Loader2, Blocks } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import type { Workspace } from '@/service/api'
import { apiService } from '@/service/api'
import { formatRelativeTime } from '@/utils/timeUtils'
import { useTranslation } from '@/i18n'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface BoardCardProps {
  board: Workspace
  handleClick: (id: string) => void
  loading: boolean
}

const BoardCard = ({ board, handleClick, loading }: BoardCardProps) => {
  const { id, name, updated_at, preview_image, created_at } = board
  const { t } = useTranslation()
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageLoading, setImageLoading] = useState(false)
  const [imageError, setImageError] = useState(false)
  const imageUrlRef = useRef<string | null>(null)
  const trimmedName = name.trim()

  useEffect(() => {
    const loadPreviewImage = async () => {
      // Reset error state on new load attempt
      setImageError(false)

      if (!preview_image) {
        setImageUrl(null)
        return
      }

      // Check if board has original (potentially corrupted) preview from a specific
      // incident in March 2025. Only boards created in that window are affected.
      const createdAt = new Date(created_at)
      const updatedAt = new Date(updated_at)
      const corruptedStart = new Date('2025-03-08T00:00:00Z')
      const corruptedEnd = new Date('2025-04-01T00:00:00Z')
      const isFromAffectedPeriod = createdAt >= corruptedStart && createdAt < corruptedEnd

      if (isFromAffectedPeriod) {
        const timeDiffSeconds = (updatedAt.getTime() - createdAt.getTime()) / 1000
        const hasOriginalPreview = timeDiffSeconds < 60
        if (hasOriginalPreview) {
          setImageUrl(null)
          return
        }
      }

      try {
        setImageLoading(true)
        const blob = await apiService.getWorkspacePreviewImage(id)
        const url = URL.createObjectURL(blob)
        setImageUrl(url)
        imageUrlRef.current = url
      } catch {
        // Silently fall back to placeholder icon — preview may not exist yet
        // (e.g., newly created board, storage sync pending, or file missing)
        setImageUrl(null)
      } finally {
        setImageLoading(false)
      }
    }

    loadPreviewImage()

    // Revoke object URL when unmounts
    return () => {
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current)
      }
    }
  }, [id, preview_image, created_at, updated_at, t])

  const titleContent = (
    <Tooltip>
      <TooltipTrigger asChild>
        <h3 className="font-medium text-sm truncate max-w-[110px]">{trimmedName}</h3>
      </TooltipTrigger>
      <TooltipContent>
        <p>{trimmedName}</p>
      </TooltipContent>
    </Tooltip>
  )

  return (
    <TooltipProvider>
      <div className="cursor-pointer w-[110px]" onClick={() => handleClick(id)}>
      <figure className="w-[110px] bg-gray-200 dark:bg-dark-border h-[64px] border hover:border-gray-200 dark:hover:border-dark-border transition-all hover:shadow-md rounded-lg flex items-center justify-center overflow-hidden relative">
        {loading || imageLoading ? (
          <Loader2 className="w-8 h-8 me-2 text-gray-400 dark:text-gray-500 animate-spin" />
        ) : imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover"
            draggable={false}
            onError={() => {
              console.warn(`Failed to render preview for board ${id}`)
              setImageError(true)
            }}
          />
        ) : (
          <Blocks className="w-8 h-8 text-gray-400 dark:text-gray-500" />
        )}
      </figure>
      <div className="flex flex-col py-2">
        {titleContent}
        <p className="text-xs text-gray-500 dark:text-dark-text-muted">
          {t('common:board.updated')}{' '}
          {formatRelativeTime(
            updated_at,
            (key: string, options?: { count?: number }) =>
              t(`common:time.${key}` as never, options)
          )}
        </p>
      </div>
    </div>
    </TooltipProvider>
  )
}

export default BoardCard
